import { Text } from "@bookingcom/bui-react";
import { t, useI18n } from "@bookingcom/lingojs-react";
import { localizationUtils } from "@flights/web-api-utils-universal";
import useFormatPrice from "../../../../hooks/useFormatPrice.js";
import useUserAgent from "../../../../hooks/useUserAgent.js";
import React, { useMemo } from "react";
import { useSelector } from "react-redux";
import { getSearchCriteria } from "../../../../store/searchCriteria/selectors.js";
import getLowestAdultTravellerPrice from "../../../../utils/lowest-adult-traveller-price.js";
import { UnifiedPriceTag } from "../../UnifiedPriceTag/index.js";
import { getAttachedAncillaries } from "../../UnifiedPriceBreakdown/index.js";
const FlightCardPriceOutbound = ({ flight }) => {
    const { isMobile } = useUserAgent();
    const i18n = useI18n();
    const { adults, children } = useSelector(getSearchCriteria);
    const { formatPrice } = useFormatPrice();
    const { priceBreakdown, travellerPrices } = flight;
    const roundPrice = true;
    const adultTravellerPrice = getLowestAdultTravellerPrice(travellerPrices);
    const travellersCount = adults + children.length;
    const isStrikedThroughPricesEnabled = flight.priceBreakdown.showPriceStrikethrough;
    const totalPrice = priceBreakdown;
    const formattedPricePerTravellerString = useMemo(() => !!adultTravellerPrice
        ? formatPrice(roundPrice && adultTravellerPrice.travellerPriceBreakdown.totalRounded
            ? adultTravellerPrice.travellerPriceBreakdown.totalRounded
            : adultTravellerPrice.travellerPriceBreakdown.total, { decimalPlaces: roundPrice ? 0 : undefined })
        : "", [adultTravellerPrice, formatPrice, roundPrice]);
    const strikedThroughPrice = useMemo(() => isStrikedThroughPricesEnabled && roundPrice && priceBreakdown.totalWithoutDiscountRounded
        ? formatPrice(priceBreakdown.totalWithoutDiscountRounded, { decimalPlaces: 0 })
        : isStrikedThroughPricesEnabled && priceBreakdown.totalWithoutDiscount
            ? formatPrice(priceBreakdown.totalWithoutDiscount)
            : null, [
        formatPrice,
        isStrikedThroughPricesEnabled,
        roundPrice,
        priceBreakdown.totalWithoutDiscount,
        priceBreakdown.totalWithoutDiscountRounded
    ]);
    const priceAriaLabel = useMemo(() => {
        const priceLabel = formattedPricePerTravellerString;
        if (localizationUtils.isHungaryDepartingRyanAirFlight(flight.segments)) {
            return `${priceLabel} ${i18n.trans(t("flights_ryanair_hungary_sr_price_single"))}`;
        }
        if (isStrikedThroughPricesEnabled && strikedThroughPrice) {
            return i18n.trans(t("a11y_flights_deal_price_new", { variables: { new_price: formattedPricePerTravellerString } }));
        }
        return priceLabel;
    }, [flight.segments, formattedPricePerTravellerString, i18n, isStrikedThroughPricesEnabled, strikedThroughPrice]);
    const formattedTotalPrice = useMemo(() => formatPrice(roundPrice && totalPrice.totalRounded ? totalPrice.totalRounded : totalPrice.total, {
        decimalPlaces: roundPrice ? 0 : undefined
    }), [formatPrice, roundPrice, totalPrice.totalRounded, totalPrice.total]);
    const priceExplanationAriaLabel = useMemo(() => {
        let priceExplanation = i18n.trans(t("flights_price_qualifier_total_for_all"));
        if (localizationUtils.isHungaryDepartingRyanAirFlight(flight.segments)) {
            priceExplanation = i18n.trans(t("flights_ryanair_hungary_bp_price_per", {
                variables: { total_price: formattedTotalPrice, num_travellers: travellersCount }
            }));
        }
        else if (isStrikedThroughPricesEnabled && priceBreakdown.totalWithoutDiscount) {
            return i18n.trans(t("a11y_flights_deal_price_total", {
                variables: {
                    total_price: formattedTotalPrice,
                    old_price: formatPrice(priceBreakdown.totalWithoutDiscount)
                }
            }));
        }
        return `${formattedTotalPrice} ${priceExplanation}`;
    }, [
        flight.segments,
        formatPrice,
        formattedTotalPrice,
        i18n,
        isStrikedThroughPricesEnabled,
        priceBreakdown.totalWithoutDiscount,
        travellersCount
    ]);
    const ariaExplanationAttributes = useMemo(() => {
        const travellersCount = adults + children.length;
        const attributes = {};
        if (travellersCount === 1) {
            attributes["aria-hidden"] = true;
        }
        else {
            attributes["aria-label"] = priceExplanationAriaLabel;
        }
        return attributes;
    }, [adults, children.length, priceExplanationAriaLabel]);
    const unifiedPriceTagProps = useMemo(() => {
        const productId = flight.unifiedPriceBreakdown?.id;
        const strikethroughPrice = priceBreakdown.showPriceStrikethrough
            ? roundPrice && priceBreakdown.totalWithoutDiscountRounded
                ? priceBreakdown.totalWithoutDiscountRounded
                : priceBreakdown.totalWithoutDiscount
            : undefined;
        const pricePerAdult = !!adultTravellerPrice
            ? roundPrice && adultTravellerPrice.travellerPriceBreakdown.totalWithoutDiscountRounded
                ? adultTravellerPrice.travellerPriceBreakdown.totalWithoutDiscountRounded
                : adultTravellerPrice.travellerPriceBreakdown.totalWithoutDiscount
            : undefined;
        const pricePerAdultExplanationProps = {
            attributes: {
                "data-testid": "flight_card_price_total_price",
                ...ariaExplanationAttributes
            }
        };
        const pricePerAdultExplanationTranslations = [
            (formattedPrice) => i18n.trans(t("flights_sr_split_bounds_us_total", {
                variables: { price: formattedPrice }
            }))
        ];
        const attachedAncillaries = getAttachedAncillaries(flight.unifiedPriceBreakdown);
        return {
            productId,
            price: roundPrice && totalPrice.totalRounded ? totalPrice.totalRounded : totalPrice.total,
            priceProps: {
                attributes: { "aria-hidden": true, "data-testid": "flight_card_price_main_price" },
                variant: "headline_3"
            },
            priceAlignment: "end",
            strikethroughPrice,
            strikethroughPriceProps: { variant: isMobile ? "body_2" : "small_1" },
            strikethroughPricePosition: "top",
            pricePerAdult,
            pricePerAdultExplanationProps,
            pricePerAdultExplanationTranslations,
            shouldShowPricePerAdult: travellersCount > 1,
            shouldRoundPrice: roundPrice,
            attachedAncillaries
        };
    }, [
        adultTravellerPrice,
        ariaExplanationAttributes,
        i18n,
        isMobile,
        priceBreakdown,
        roundPrice,
        totalPrice,
        travellersCount,
        flight.unifiedPriceBreakdown
    ]);
    return (React.createElement("div", null,
        React.createElement(Text, { variant: "small_1", color: "neutral_alt" }, travellersCount > 1
            ? i18n.trans(t("flights_sr_split_bounds_us_round_trip_per"))
            : i18n.trans(t("flights_sr_split_bounds_us_round_trip"))),
        React.createElement(UnifiedPriceTag, { ...unifiedPriceTagProps, attributes: { role: "region", "aria-label": priceAriaLabel } })));
};
export default FlightCardPriceOutbound;
